import {t} from "@lingui/core/macro";
import * as React from "react";
import {Redirect, Route} from "react-router-dom";

import {PermissionsInfo, RequestInfo} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import {TEACHER_KHANMIGO_TOOLS_PATH} from "#coach-dashboard-package/khanmigo/path-helpers.ts";
import PageDisplay from "#data-access/page-display.tsx";
import useProfile from "#data-access/use-profile.ts";
import {useLearnPrototypeEnabled} from "#learn-prototype/use-learn-prototype-enabled.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";

import RouteLoadSpinner from "./route-load-spinner.tsx";
import TopLevelRoute from "./top-level-route.tsx";

const LiteHomepage = React.lazy(
    () => import("#lite-homepage-package/shared-lite-homepage.tsx"),
);
const LoggedOutHomepage = React.lazy(
    () =>
        import(
            "#logged-out-homepage-package/logged-out-homepage-module-list.tsx"
        ),
);
const LoggedInHomepage = React.lazy(
    () => import("#logged-in-homepage-package/logged-in-homepage.tsx"),
);

// sync-start:lite-ka-locales 1742132507 https://github.com/Khan/webapp/blob/master/pkg/services_shared/intl/ka_locales.go
const liteLanguages = ["fa", "fa-af", "he", "ms", "sw", "th", "xh", "zu"];
// sync-end:lite-ka-locales

/**
 * Props for the HomepageRoute component.
 *
 * NOTE(jeff): We keep route info as a prop so that it is easy to scan routing
 * in the top level route switch.
 */
type Props = {
    exact: boolean;
    path: string;
};

/**
 * Renders the logged in or logged out homepage for the given route.
 */
export default function HomepageRoute(props: Props): React.ReactElement {
    const profileData = useProfile();
    const learnPrototypeEnabled = useLearnPrototypeEnabled();
    const location = useLocation();

    const profile =
        profileData.status === "success" ? profileData.profile : null;

    // See if we should render the 'lite' homepage (used for languages we
    // don't have fully translated yet) rather than the full homepage.
    const isLiteLocale =
        !RequestInfo.usePreviewFMS &&
        liteLanguages.includes(RequestInfo.kaLocale);

    if (isLiteLocale) {
        return <TopLevelRoute {...props} component={LiteHomepage} />;
    }

    // If the user is not logged in, send them to the Logged OUT Homepage
    if (!PermissionsInfo.isLoggedIn && !props.path.startsWith("/profile")) {
        return <TopLevelRoute {...props} component={LoggedOutHomepage} />;
    }

    // Check to see if this is the default homepage (/).  If not, then we
    // can just load this normally.
    if (props.path !== "/") {
        return <TopLevelRoute {...props} component={LoggedInHomepage} />;
    }

    if (learnPrototypeEnabled && props.path === "/") {
        return (
            <TopLevelRoute
                {...props}
                component={() => <Redirect to="/learn" />}
            />
        );
    }

    // Wait for the profile since the LIHP is heavily depending on it.
    if (!profile) {
        return (
            <PageDisplay settings={{title: t`Dashboard`}}>
                <Route path={props.path} render={() => <RouteLoadSpinner />} />
            </PageDisplay>
        );
    }

    // Based on the user's profile, we might need to redirect them to
    // their default homepage route.  If the default homepage URL is /
    // or ?learn=1 is defined, then we need to show the Logged in homepage.
    // Otherwise redirect
    const homepageUrl = getHomepageUrl(profile);
    return (
        <TopLevelRoute
            {...props}
            component={() =>
                !location.queryParams.learn && homepageUrl !== "/" ? (
                    <SmartRedirect to={homepageUrl} />
                ) : (
                    <LoggedInHomepage />
                )
            }
        />
    );
}

//  Grassroots Teachers that have access to the AI Guide
// should see the Khanmigo Tools page. KAD or KMAP teachers should see the
// default homepage (teacher dashboard) regardless of whether they have
// access to the AI Guide.
// TODO: Add the Khanmigo activities as a homepage option in user settings
function getHomepageUrl(profile: ProfileModelType): string {
    const belongsToDistrict = profile.get("includesDistrictOwnedData");
    const hasKhanmigo = profile.get("isAIGuideEnabled");
    const hasAccessToAIGuideTeacher = profile.get("hasAccessToAIGuideTeacher");

    // KAD or KMAP teachers should always see the default
    // teacher homepage.
    if (
        belongsToDistrict &&
        !profile.get("isKmapStudent") &&
        !profile.get("isK4dStudent")
    ) {
        return profile.get("homepageUrl");
    }

    // Grassroots Khanmigo teachers should see the Khanmigo Tools page.
    if (hasKhanmigo && hasAccessToAIGuideTeacher) {
        return TEACHER_KHANMIGO_TOOLS_PATH;
    }

    // Everyone else sees their default homepage.
    return profile.get("homepageUrl");
}
